import {
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  DatePicker,
  Tooltip,
} from 'antd';
import { InfoCircle } from 'react-bootstrap-icons';
import { FormButton, FormField, FormTitle, FormNumber } from '../form/FormBasic';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { CheckCircleFilled, QrcodeOutlined } from '@ant-design/icons';
import BarcodeScanner from '../common/BarcodeScanner';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_NUMBERS_SAFE_FILTER } from '../../constants/regex';
import { noSpacesOnlyRule, maxLengthRule, regexRule, requiredRule } from '../../helpers/formHelper';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import OfflineSyncWarning from '../common/OfflineSyncWarning';

interface IProduct {
  name: string;
  productCategory: string;
  subCategory: string;
  unit: string;
  basketIds: number[];
  presentation?: string;
  brand?: string;
  barcode?: string;
}

interface AddProductFormProps {
  form: FormInstance;
  product?: IProduct;
  handleCancel: () => void;
  isModal?: boolean;
  getConfirmation?: () => void;
  isEdit?: boolean;
  initialCategoryOptions?: any[];
  isEditDataLoading?: boolean;
  productId?: string;
}

const AddProductForm: React.FC<AddProductFormProps> = ({
  form,
  handleCancel,
  isModal,
  getConfirmation,
  isEdit,
  initialCategoryOptions,
  isEditDataLoading,
  productId,
}) => {
  const { t } = useTranslation(['product', 'form']);
  const { errorNotification } = useNotificationContext();
  const { isOnline } = useIsOnlineContext();

  const [mainCategoryOptions, setMainCategoryOptions] = useState<any>();

  const [basketOptions, setBasketOptions] = useState<any>();
  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [isBarcodeScanned, setIsBarcodeScanned] = useState(false);

  const [hasCustomFields, setHasCustomFields] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<any>();

  const [mainProducts, setMainProducts] = useState<any[]>([]);
  const [availablePresentations, setAvailablePresentations] = useState<any[]>([]);
  const [availableBrands, setAvailableBrands] = useState<any[]>([]);
  const [showCustomPresentation, setShowCustomPresentation] = useState(false);
  const [showCustomBrand, setShowCustomBrand] = useState(false);
  const [unitGroups, setUnitGroups] = useState<any[]>([]);
  const [availableUnitOptions, setAvailableUnitOptions] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [isOfflineAndEmpty, setIsOfflineAndEmpty] = useState<boolean>(false);

  const handleScanSuccess = (decodedText: string) => {
    form.setFieldValue('barcode', decodedText);
    setIsBarcodeScanned(true);
    setIsScannerModalOpen(false);
  };

  const isInitializingRef = useRef(false);

  const getProductCategoryData = async (productCategoryId: string) => {
    return await referenceDataSyncService.getProductCategoryDataById(productCategoryId);
  };

  const productCategoryChange = Form.useWatch('productCategory', form);

  useEffect(() => {
    const init = async () => {
      if (isEdit) {
        setHasCustomFields(false);

        const productCategoryId = form.getFieldValue('productCategory');

        if (productCategoryId) {
          const categoryData: any = await getProductCategoryData(productCategoryId);

          if (categoryData?.[0]?.customFields) {
            setCustomFields(categoryData[0].customFields.fields);
            setHasCustomFields(true);
          }
        }
      }

      const groups = await referenceDataSyncService.getConvertibleUnitGroups();
      setUnitGroups(groups);
    };

    init();
  }, [productCategoryChange]);

  useEffect(() => {
    let active = true;
    let intervalId: any = null;

    const loadAllOptions = async () => {
      try {
        setLoadingOptions(true);
        setIsOfflineAndEmpty(false);

        // 1. Get baskets
        await getBasketsData();

        // 2. Get product categories
        await getProductCategories();

        // 3. Get main products
        const mains = await referenceDataSyncService.getMainProducts();
        const mappedMains = mains.map((p) => ({
          label: p.name,
          value: p.id,
          id: p.id,
          unit: p.unit,
          productCategoryId: p.productCategory?.id,
          presentations: p.presentations,
          brands: p.brands,
        }));

        if (active) {
          setMainProducts(mappedMains);

          // If online and have no data, keep loading
          if (isOnline && mappedMains.length === 0) {
            setLoadingOptions(true);
          } else {
            setLoadingOptions(false);
            if (!isOnline && mappedMains.length === 0) {
              setIsOfflineAndEmpty(true);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load options', err);
        if (active) setLoadingOptions(false);
      }
    };

    loadAllOptions();

    if (isOnline) {
      intervalId = setInterval(async () => {
        if (!active) return;
        const localMainProducts = await referenceDataSyncService.getMainProducts();
        if (localMainProducts.length > 0) {
          await loadAllOptions();
          clearInterval(intervalId);
        }
      }, 2000);
    }

    return () => {
      active = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isOnline]);

  useEffect(() => {
    if (isEdit && !isInitializingRef.current) {
      isInitializingRef.current = true;
      isInitializingRef.current = false;
    }
  }, [isEdit]);

  const mainProductChange = Form.useWatch('mainProductName', form);
  const presentationChange = Form.useWatch('presentation', form);
  const customPresentationChange = Form.useWatch('customPresentation', form);
  const brandChange = Form.useWatch('brand', form);
  const customBrandChange = Form.useWatch('customBrand', form);

  useEffect(() => {
    if (mainProductChange && mainProducts.length > 0) {
      const selectedProductData = mainProducts.find((m) => m.value === mainProductChange);

      if (selectedProductData) {
        const presOptions = (selectedProductData.presentations || [])
          .filter((p: any) => p.isActive !== false)
          .map((p: any) => ({ label: p.name, value: p.id }));

        if (
          isEdit &&
          presentationChange &&
          presentationChange !== 'Other' &&
          !presOptions.some((p: any) => p.value === presentationChange)
        ) {
          // In edit mode, if the assigned presentation is missing, add it using the ID provided
          const label =
            selectedProductData.presentations?.find((p: any) => p.id === presentationChange)
              ?.name || presentationChange;
          presOptions.push({ label: label, value: presentationChange });
        }
        presOptions.push({ label: 'Other', value: 'Other' });
        setAvailablePresentations(presOptions);

        const brandOptions = (selectedProductData.brands || [])
          .filter((b: any) => b.isActive !== false)
          .map((b: any) => ({ label: b.name, value: b.id }));

        if (
          isEdit &&
          brandChange &&
          brandChange !== 'Other' &&
          !brandOptions.some((b: any) => b.value === brandChange)
        ) {
          const label =
            selectedProductData.brands?.find((b: any) => b.id === brandChange)?.name || brandChange;
          brandOptions.push({ label: label, value: brandChange });
        }
        brandOptions.push({ label: 'Other', value: 'Other' });
        setAvailableBrands(brandOptions);

        // Only auto-fill unit/category in create mode, not edit mode
        if (!isEdit) {
          form.setFieldValue('mainProductId', selectedProductData.id);
          form.setFieldValue('unit', selectedProductData.unit);
          if (selectedProductData.productCategoryId) {
            form.setFieldValue('productCategory', selectedProductData.productCategoryId.toString());
            form.setFieldValue('productCategoryId', selectedProductData.productCategoryId);
            handleMainCategoryChange(selectedProductData.productCategoryId.toString());
          }
        }
      }
    } else {
      setAvailablePresentations([{ label: 'Other', value: 'Other' }]);
      setAvailableBrands([{ label: 'Other', value: 'Other' }]);
      setAvailableUnitOptions([]);
    }
  }, [mainProductChange, mainProducts, isEdit, presentationChange, brandChange, form, unitGroups]);

  useEffect(() => {
    if (mainProductChange && mainProducts.length > 0) {
      const mainProduct = mainProducts.find((m) => m.value === mainProductChange);
      if (mainProduct && mainProduct.unit) {
        const activeGroup = unitGroups.find((g) => g.units && g.units.includes(mainProduct.unit));
        const allowedUnits = activeGroup ? activeGroup.units : [mainProduct.unit];
        setAvailableUnitOptions(
          allowedUnits.map((u: string) => ({
            label: t(`form:measuringUnits.${u}`),
            value: u,
          }))
        );
      } else {
        setAvailableUnitOptions([]);
      }
    } else {
      setAvailableUnitOptions([]);
    }
  }, [mainProductChange, mainProducts, unitGroups, t]);

  // Auto-generate name and labels
  useEffect(() => {
    // Look up main product label
    const mainProduct = mainProducts.find((m) => m.value === mainProductChange);
    let generatedName = mainProduct ? mainProduct.label : '';

    // Look up presentation label
    let presValue = '';
    if (presentationChange === 'Other') {
      presValue = customPresentationChange;
    } else {
      const presOption = availablePresentations.find((p) => p.value === presentationChange);
      presValue = presOption ? presOption.label : '';
    }
    if (presValue) {
      generatedName += ` ${presValue}`;
    }

    // Look up brand label
    let brandValue = '';
    if (brandChange === 'Other') {
      brandValue = customBrandChange;
    } else {
      const brandOption = availableBrands.find((b) => b.value === brandChange);
      brandValue = brandOption ? brandOption.label : '';
    }
    if (brandValue) {
      generatedName += ` ${brandValue}`;
    }

    if (!isEdit) {
      form.setFieldValue('name', generatedName.trim());
    }
    form.setFieldValue('mainProductLabel', mainProduct ? mainProduct.label : '');
    form.setFieldValue('presentationLabel', presValue);
    form.setFieldValue('brandLabel', brandValue);
  }, [
    mainProductChange,
    presentationChange,
    customPresentationChange,
    brandChange,
    customBrandChange,
    availablePresentations,
    availableBrands,
    mainProducts,
    form,
    isEdit,
  ]);

  useEffect(() => {
    setShowCustomPresentation(presentationChange === 'Other');
  }, [presentationChange]);

  useEffect(() => {
    setShowCustomBrand(brandChange === 'Other');
  }, [brandChange]);

  useEffect(() => {
    if (initialCategoryOptions && initialCategoryOptions.length > 0) {
      setMainCategoryOptions(initialCategoryOptions);
    }
  }, [initialCategoryOptions]);

  const getBasketsData = async () => {
    try {
      const allBaskets = await referenceDataSyncService.getBaskets();
      const basketOptionArr = allBaskets.map((basket) => ({
        label: basket.name,
        value: basket.id,
        id: basket.id,
      }));

      setBasketOptions(basketOptionArr);
    } catch (error) {
      console.error('Error loading baskets:', error);
    }
  };

  const getProductCategories = async () => {
    try {
      const categoriesData = await referenceDataSyncService.getProductCategories();
      const categoryOptionArr = categoriesData.map((category: any) => ({
        label: `${category.mainCategory}${category.category ? ` > ${category.category}` : ''}`,
        value: category?.id?.toString(),
        id: category?.id,
      }));

      setMainCategoryOptions(categoryOptionArr);
    } catch (error) {
      console.error('Error loading product categories:', error);
      setMainCategoryOptions([]);
    }
  };

  const handleMainCategoryChange = async (val: any) => {
    form.setFieldValue('customFields', {});

    setHasCustomFields(false);
    setCustomFields([]);
    if (val && mainCategoryOptions) {
      const selectedCategory = mainCategoryOptions.find((option: any) => option.value === val);
      if (selectedCategory) {
        form.setFieldValue('productCategoryId', selectedCategory.id);
      }

      const categoryData: any = await referenceDataSyncService.getProductCategoryDataById(val);

      if (categoryData[0]?.customFields) {
        setCustomFields(categoryData[0]?.customFields?.fields);
        setHasCustomFields(true);
      }
    } else {
      form.setFieldValue('productCategoryId', undefined);
    }
  };

  const onUnitOfMeasurementChange = () => {};

  const onBasketChange = async (selectedValues: number[]) => {
    form.setFieldValue('basketIds', selectedValues);
  };

  const renderInputByType = (field: any) => {
    switch (field.dataType) {
      case 'number':
        return (
          <InputNumber
            style={{ width: '100%' }}
            placeholder={field.placeholder}
            disabled={isEdit}
            // step={1}
            precision={0}
            // type="number"
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={(e) => {
              // Allow negative and positive integers. dash is allowed only at the beginning. digits allowed.
              // const input = e.target as HTMLInputElement;
              const isDigit = /[0-9]/.test(e.key);
              const isControlOrNavKey = [
                'Backspace',
                'Delete',
                'Tab',
                'Escape',
                'Enter',
                'Home',
                'End',
                'ArrowLeft',
                'ArrowRight',
              ].includes(e.key);

              if (isDigit || isControlOrNavKey) {
                return;
              }
              // - can be only at the beginning for negative values
              if (
                e.key === '-' &&
                (e.target as HTMLInputElement).selectionStart === 0 &&
                !(e.target as HTMLInputElement).value.includes('-')
              ) {
                return;
              }

              e.preventDefault();
            }}
            /* if (
            e.key === 'e' ||
            e.key === 'E' ||
            e.key === '.' ||
            (e.key === '-' && (input.value.includes('-') || input.selectionStart !== 0)) ||
            e.key === '+'
          ) {
            e.preventDefault();
          } */

            // const allowedKeys = [
            //   'Backspace',
            //   'Enter',
            //   'Escape',
            //   'ArrowLeft',
            //   'ArrowRight',
            //   'Delete',
            //   'End',
            //   'Home',
            // ];

            // const isDigit = /[0-9]/.test(e.key);
            // if (!allowedKeys.includes(e.key) && !isDigit) {
            //   e.preventDefault();
            // }
            // }}
            // formatter={(value) => `${value}`.replace(/\D/g, '')}
          />
        );
      case 'float':
        return (
          <InputNumber
            style={{ width: '100%' }}
            type="number"
            step={0.01}
            precision={2}
            min={0}
            placeholder={field.placeholder}
            disabled={isEdit}
            onWheel={(e) => e.currentTarget.blur()}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              // Allow one fullstop, 2 decimal place. digits allowed
              if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                e.preventDefault();
              }
              if (e.key === '.' && String((e.target as HTMLInputElement).value).includes('.')) {
                e.preventDefault();
              }
            }}
            formatter={(value) => {
              if (!value) return '';
              return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
            }}
          />
        );
      case 'date':
        return <DatePicker style={{ width: '100%' }} inputReadOnly={true} disabled={isEdit} />;
      case 'text':
        return (
          <Input.TextArea
            style={{ width: '100%' }}
            placeholder={field.placeholder}
            disabled={isEdit}
          />
        );
      default:
        return (
          <Input style={{ width: '100%' }} placeholder={field.placeholder} disabled={isEdit} />
        );
    }
  };

  const selectedBaskets = Form.useWatch('baskets', form);

  return (
    <>
      {!isModal && (
        <div className="text-center">
          <FormTitle label={'Add Product'} />
        </div>
      )}
      {isEditDataLoading && (
        <div className="flex justify-center">
          <Spin />
        </div>
      )}

      <Form
        layout="vertical"
        form={form}
        name="addProductForm"
        onFinish={getConfirmation}
        className="mt-2 w-full"
      >
        {isOfflineAndEmpty && <OfflineSyncWarning />}

        {/* Main Product */}
        <Form.Item
          name="mainProductName"
          label={t('productName')}
          rules={[requiredRule(t, t('productName'))]}
        >
          <Select
            loading={loadingOptions}
            disabled={isEdit || loadingOptions || isOfflineAndEmpty}
            placeholder={loadingOptions ? t('form:loadingData') : t('productFieldPlaceholder')}
            options={mainProducts}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            onChange={() => {
              form.setFieldsValue({
                presentation: undefined,
                customPresentation: undefined,
                brand: undefined,
                customBrand: undefined,
                equivalentAmount: undefined,
                barcode: undefined,
              });
            }}
          />
        </Form.Item>

        {/* Suggested Presentation */}
        <Form.Item
          name="presentation"
          label={t('suggestedPresentation')}
          rules={[requiredRule(t, t('suggestedPresentation'))]}
        >
          <Select
            disabled={isEdit}
            placeholder={t('suggestedPresentationFieldPlaceholder')}
            options={availablePresentations}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {showCustomPresentation && (
          <FormField
            name="customPresentation"
            label={t('customPresentation')}
            rules={[
              requiredRule(t, t('suggestedPresentation')),
              noSpacesOnlyRule(t, t('customPresentation')),
              maxLengthRule(t, t('customPresentation'), 120),
              regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') return Promise.resolve();
                  const isDuplicate = availablePresentations.some(
                    (p: any) =>
                      p.value !== 'Other' && p.label.toLowerCase() === value.trim().toLowerCase()
                  );
                  if (isDuplicate) {
                    return Promise.reject(t('duplicatePresentationError'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            placeholder={t('suggestedPresentationFieldPlaceholder')}
            disabled={isEdit}
            type={FormFieldTypeEnum.INPUT}
            isRequired={true}
          />
        )}

        {/* Suggested Brand */}
        <Form.Item name="brand" label={t('suggestedBrand')}>
          <Select
            disabled={isEdit}
            placeholder={t('suggestedBrandFieldPlaceholder')}
            options={availableBrands}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {showCustomBrand && (
          <FormField
            name="customBrand"
            label={t('customBrand')}
            rules={[
              requiredRule(t, t('suggestedBrand')),
              noSpacesOnlyRule(t, t('customBrand')),
              maxLengthRule(t, t('customBrand'), 60),
              regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') return Promise.resolve();
                  const isDuplicate = availableBrands.some(
                    (b: any) =>
                      b.value !== 'Other' && b.label.toLowerCase() === value.trim().toLowerCase()
                  );
                  if (isDuplicate) {
                    return Promise.reject(t('duplicateBrandError'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            placeholder={t('suggestedBrandFieldPlaceholder')}
            disabled={isEdit}
            type={FormFieldTypeEnum.INPUT}
            isRequired={true}
          />
        )}

        {/* Auto Generated Name */}
        <Form.Item name="name" label={t('generatedName')}>
          <Input disabled={true} />
        </Form.Item>

        {/* Hidden Label Fields for Payload Extraction */}
        <Form.Item name="mainProductLabel" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="presentationLabel" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="brandLabel" hidden>
          <Input />
        </Form.Item>

        {/* Unit (Auto-filled but editable) */}
        <Form.Item name="unit" label={t('unitOfMeasurement')}>
          <Select options={availableUnitOptions} placeholder={t('unitPlaceholder')} />
        </Form.Item>

        {/* Barcode */}
        <FormField
          name="barcode"
          label={t('Barcode')}
          rules={[
            {
              validator: async (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                const barcodeExists = await referenceDataSyncService.checkBarcodeExists(
                  value,
                  productId
                );

                if (barcodeExists) {
                  return Promise.reject(t('barcodeAlreadyExists'));
                }

                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (value && value.trim() === '') {
                  return Promise.reject(t('barcodeSpaceValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                // Allow only digits
                const regex = REGEX_NUMBERS_SAFE_FILTER;
                return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength < 5) {
                  return Promise.reject(t('barcodeMinValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength > 15) {
                  return Promise.reject(t('barcodeMaxValidation'));
                }
                return Promise.resolve();
              },
            },
          ]}
          placeholder={t('BarcodePlaceholder')}
          disabled={false}
          type={FormFieldTypeEnum.INPUT}
          isRequired={false}
          allowLeadingTrailingSpaces={false}
          // maxLength={15}
          addonAfter={
            <QrcodeOutlined
              style={{ fontSize: '22px', cursor: 'pointer' }}
              onClick={() => setIsScannerModalOpen(true)}
            />
          }
          suffix={isBarcodeScanned ? <CheckCircleFilled style={{ color: 'green' }} /> : null}
          onChange={() => {
            if (isBarcodeScanned) {
              setIsBarcodeScanned(false);
            }
          }}
        />

        <Modal
          title="Scan Barcode"
          open={isScannerModalOpen}
          onCancel={() => setIsScannerModalOpen(false)}
          footer={null}
          destroyOnClose
          centered
        >
          {isScannerModalOpen && (
            <BarcodeScanner
              onScanSuccess={handleScanSuccess}
              onScanFailure={() => {}}
              scannerId="reader_product"
              errorNotification={errorNotification}
            />
          )}
        </Modal>

        {/* Category (Auto-filled) */}
        <Form.Item name="productCategory" label={t('category', 'Category')}>
          <Select
            disabled={true}
            options={mainCategoryOptions}
            placeholder={t('categoryFieldPlaceholder')}
          />
        </Form.Item>

        {/* Equivalent Amount */}
        <FormNumber
          name="equivalentAmount"
          label={
            <span className="flex items-center">
              {t('equivalentAmount')}
              <Tooltip
                title={<div style={{ whiteSpace: 'pre-wrap' }}>{t('equivalentAmountInfo')}</div>}
              >
                <InfoCircle className="ml-2 inline cursor-help text-blue-500" />
              </Tooltip>
            </span>
          }
          rules={[
            requiredRule(t, t('equivalentAmount')),
            {
              validator: (_, value) => {
                if (value === undefined || value === null) return Promise.resolve();
                if (isNaN(Number(value))) return Promise.reject(t('invalidInput'));
                if (Number(value) < 0) return Promise.reject(t('invalidInput'));
                return Promise.resolve();
              },
            },
          ]}
          placeholder={t('equivalentAmount')}
          disabled={false}
          type="float"
          isRequired={true}
        />

        {hasCustomFields && (
          <>
            {customFields?.map((field: any) => {
              return (
                <Form.Item
                  key={field.key.toString()}
                  name={['customFields', field.key.toString()]}
                  label={field.fieldName}
                  initialValue={null}
                  validateFirst
                  rules={[
                    {
                      required: field.required,
                      message: `${t('isRequired', { field: field.fieldName })}`,
                    },
                    {
                      validator: (_, value) => {
                        if (
                          (field.dataType === 'string' || field.dataType === 'text') &&
                          value &&
                          value.trim() === ''
                        ) {
                          return Promise.reject(t('spaceValidation', { field: field.fieldName }));
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_: any, value: any) => {
                        if (value === null || value === undefined || value === '')
                          return Promise.resolve();

                        if (field.dataType === 'string' || field.dataType === 'text') {
                          // Allow letters, numbers, space, punctuation and special characters
                          const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                          return regex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(t('invalidInput'));
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        // minimum validation for string and textarea
                        if (field.dataType === 'string' || field.dataType === 'text') {
                          if (!value || value.trim() === '') {
                            return Promise.resolve();
                          }

                          const trimmedLength = value.replace(/\s/g, '').length;
                          if (trimmedLength < 1) {
                            return Promise.reject(t('minValidation', { field: field.fieldName }));
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        // maximum validation for string and textarea
                        if (field.dataType === 'string' || field.dataType === 'text') {
                          if (!value || value.trim() === '') {
                            return Promise.resolve();
                          }

                          const trimmedLength = value.replace(/\s/g, '').length;
                          if (field.dataType === 'text' && trimmedLength > 200) {
                            return Promise.reject(
                              t('maxValidationTextarea', { field: field.fieldName })
                            );
                          }
                          if (field.dataType === 'string' && trimmedLength > 100) {
                            return Promise.reject(
                              t('maxValidationString', { field: field.fieldName })
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        if (field.dataType === 'number') {
                          // MAX_SAFE_INTEGER is 9007199254740991
                          // MIN_SAFE_INTEGER is −9007199254740991
                          if (value > Number.MAX_SAFE_INTEGER || value < Number.MIN_SAFE_INTEGER) {
                            return Promise.reject(new Error(t('invalidInput')));
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, value) => {
                        if (field.dataType === 'float') {
                          // MAX_VALUE is 1.79E+308
                          // value > Number.MAX_VALUE
                          if (value > 9999999999999.99) {
                            return Promise.reject(new Error(t('invalidInput')));
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {renderInputByType(field)}
                </Form.Item>
              );
            })}
          </>
        )}

        <Form.Item name="basketIds" label="Basket IDs" hidden>
          <Input hidden />
        </Form.Item>

        <Form.Item name="productCategoryId" hidden>
          <Input hidden />
        </Form.Item>

        <Form.Item name="mainProductId" hidden>
          <Input hidden />
        </Form.Item>

        {!isModal && (
          <div className="mx-auto mt-6 flex items-center justify-between gap-6">
            <FormButton
              label={t('cancel')}
              type="button"
              disabled={false}
              onClick={handleCancel}
              className="btn-default w-1/2 rounded-2xl"
            />
            <FormButton
              label={t('submit')}
              type="submit"
              disabled={false}
              className="btn-primary w-1/2 rounded-2xl"
            />
          </div>
        )}
      </Form>
    </>
  );
};

export default AddProductForm;
